import pandora from "@faizaanceg/pandora";
import { Injection, Syringe } from "@faizaanceg/syringe";
import { RUM } from "@webpro/monitoring";
import { BrandConfigurationOperations } from "common/brand-configuration";
import { createEmbassySession } from "common/embassy/session";
import { parseSearch } from "common/qs";
import { findRuntimeSettings } from "common/runtime";
import { SiteDetailsManager } from "common/site-details";
import { isObSandbox, oboxApiPrefix } from "constants/ob-sandbox";

const runtime = await findRuntimeSettings();

try {
  const { initFaro } = await import("@webpro/monitoring/faro-bootstrap");
  initFaro(runtime);
} catch (error) {
  console.warn("[Faro] initialization failed; observability disabled", error);
}

const CreatePortalContainer: Injection<"portalContainer"> = {
  name: "portalContainer",
  injectFn: () => document.body,
  uses: []
};

const RuntimeSettings: Injection<"runtime"> = {
  name: "runtime",
  injectFn: () => runtime,
  uses: []
};

const ExternalAccessManagerFactory: Injection<"externalAccessManager"> = {
  name: "externalAccessManager",
  injectFn: ({ runtime }) => ({
    createPaymentUrl(key) {
      const hostname = `${
        typeof window !== "undefined" ? window.location.origin : ""
      }${runtime.backend}`;
      return `${hostname}webhooks/payments/complete/${key}`;
    },
    createWindow(target) {
      return window.open("/loader.html", target);
    },
    createCurrentHost() {
      return "";
    }
  }),
  uses: [{ name: "runtime" }]
};

function getDefaultUrls(env: string) {
  switch (env) {
    /**
     * We prioritize production urls for local dev
     */
    case "development":
      return "demo.webpropanel.com";
    /**
     * We prioritize staging urls for PR instances
     */
    case "staging":
      return "staging-colo-provo.myorderbox.com";
    case "canary":
    default:
      return window.location.hostname;
  }
}

const SiteUrlProviderFactory: Injection<"siteUrlProvider"> = {
  name: "siteUrlProvider",
  injectFn: ({ runtime }) => {
    if (runtime.environment === "production") {
      return () => window.location.hostname;
    }
    return () => {
      const qs = parseSearch(window.location.search);
      return (
        qs.url ??
        pandora.get("parentbrandingurl", getDefaultUrls(runtime.environment))
      );
    };
  },
  uses: [{ name: "runtime" }]
};

Syringe.fill([
  CreatePortalContainer,
  RuntimeSettings,
  ExternalAccessManagerFactory,
  SiteUrlProviderFactory
]);

const { AppCredentialsManager, SessionManagerBean } = await import(
  /* webpackChunkName: "credentials" */ "./credentials"
);

Syringe.fill([
  SessionManagerBean.register(createEmbassySession("superpanel")),
  SiteDetailsManager.register(),
  AppCredentialsManager.register(true)
]);

let UserConstraintsFactory: Injection<"userConstraints">;

const siteManager = Syringe.inject("siteManager");
const siteUrlProvider = Syringe.inject("siteUrlProvider");
const details = await siteManager.details();

const hostname = siteUrlProvider();
const obox = oboxApiPrefix(isObSandbox(details?.resellerid, hostname));

Syringe.fill([
  {
    name: "apiUrls",
    injectFn: ({ runtime }) => ({
      apiHost: `${runtime.gateway}${obox}`,
      cacheableApiHost: `${runtime.gateway}${obox}`,
      searchURL: `${runtime.gateway}plastic/`
    }),
    uses: [{ name: "runtime" }]
  }
]);

if (details) {
  const brandConfiguration = BrandConfigurationOperations.findById(
    details?.resellerid,
    "customer"
  );
  if (brandConfiguration?.preferredUserRole === "reseller") {
    document.title = "WebPro Panel";
  }
  UserConstraintsFactory = {
    name: "userConstraints",
    injectFn: ({ runtime }) => ({
      addFundsOnDemand: true,
      useLegacyPayment: false,
      enableLogin: true,
      enableSmartCart: true,
      manageAutoRenewal: true,
      useConsolidatedPricing: brandConfiguration?.brand === "bigrock",
      hasRecentSearches: brandConfiguration?.preferredUserRole === "reseller",
      enableAtlasChat: brandConfiguration?.brand === "bigrock",
      enableCustomerIO:
        brandConfiguration?.brand === "bigrock" &&
        ["canary"].includes(runtime.environment),
      manageSitesNative: true
    }),
    uses: [{ name: "runtime" }]
  };
  if (brandConfiguration?.brand === "bigrock") {
    const { AtlasSDK } = await import("common/rocktalk/sdk");
    AtlasSDK.setBrand("bigrock");
  }
  if (["production", "canary"].includes(runtime.environment)) {
    const analytics = BrandConfigurationOperations.findAnalyticsId(
      brandConfiguration?.brand
    );
    const abTestingId = BrandConfigurationOperations.findABTestingId(
      brandConfiguration?.brand
    );
    let abTesting = null;
    if (abTestingId) {
      abTesting = {
        id: abTestingId,
        url: `${window.location.origin}/static/js/vendor/at.js`
      };
    }
    RUM.init({ analytics, abTesting, rum: null });
  }
  document
    .querySelector(":root")
    ?.setAttribute("brand", brandConfiguration?.brand ?? "resellerclub");
} else {
  console.error(
    "No site details found, using default user constraints. This is not expected to happen."
  );
  UserConstraintsFactory = {
    name: "userConstraints",
    injectFn: () => ({
      addFundsOnDemand: true,
      useLegacyPayment: false,
      enableLogin: true,
      enableSmartCart: true,
      manageAutoRenewal: true,
      useConsolidatedPricing: false,
      hasRecentSearches: false,
      enableAtlasChat: false,
      enableCustomerIO: false,
      manageSitesNative: true
    }),
    uses: []
  };
}

Syringe.fill([UserConstraintsFactory]);

await import(/* webpackChunkName: "app" */ "../index");
